import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  timeout,
  distinctUntilChanged,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { Component, Input, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import { saleSelectors } from '../sale.selectors'
import { SaleItem } from '../sale-item/sale-item'
import { saleItemSelectors } from '../sale-item/sale-item.selectors'
import { SaleItemShowModalComponent } from '../sale-item/show-modal/show.component'
import { BsModalService } from 'ngx-bootstrap/modal'
import { SaleItemCreateModalComponent } from '../sale-item/create-modal/show.component'
import { Sale, StatusCode } from '../sale'
import { saleItemActions } from '../sale-item/sale-item.actions'
import { NotificationService } from 'src/app/core/notification.service'
import { Actions, ofType } from '@ngrx/effects'
import { paymentSelectors } from '../payment/payment.selectors'
import { CollectStatusType, PayMethod } from '../payment/payment'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { saleActions } from '../sale.actions'
import { participationSelectors } from '../../booking/participation/participation.selectors'
import { SaleItemsGroupByProductPipe } from 'src/app/shared/components/dms-table/table-pipes/sale-item-group-by-product.pipe'
import { memberSelectors } from '../../member/member.selectors'
import moment from 'moment-mini'
import { TranslateService } from '@ngx-translate/core'
import { organizationSelectors } from '../../organization/organization.selectors'
import { bookingSelectors } from '../../booking/booking.selectors'
import { bookingItemSelectors } from '../../booking/booking-item/booking-item.selectors'
import { environment } from 'src/environments/environment'
import { BulkAction, BulkActionEvent } from 'src/app/shared/interfaces'
import { SaleItemService } from '../sale-item/sale-item.service'
import { paymentActions } from '../payment/payment.actions'
import { Router } from '@angular/router'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { SaleService } from '../sale.service'
import { Product } from '../../product/product'
import { Dictionary } from '@ngrx/entity'
import { productSelectors } from '../../product/product.selectors'
import { SaleItemEditsModalComponent } from 'src/app/shared/components/dms-table/table-views/sale-item/sale-item-edits-modal/sale-item-edits-modal.component'
import { SaleItemExtended } from 'src/app/shared/components/dms-table/table-views/sale-item/sale-item-view.component'
import { configurationOptionSelectors } from '../../configuration-option/configuration-option.selectors'
import { ConfigOptionsKeys } from 'src/app/shared/constants'

@UntilDestroy()
@Component({
  selector: 'sale-sale-item-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleSaleItemTabComponent implements OnInit {
  @Input() saleId: number
  @Input() readonly: boolean = false
  public saleId$: Observable<number>
  public saleItems$: Observable<SaleItem[]>
  public resetAllChecked$: Subject<any> = new BehaviorSubject<any>([
    null,
    false
  ])
  public productEntities$: Observable<Dictionary<Product>>
  public sale$: Observable<Sale>
  public resetTableData$: Subject<boolean> = new Subject<boolean>()
  public saleStatusCodes = StatusCode
  public isLoading$ = this.store.select(saleItemSelectors.selectIsLoading)
  public accessRights = AccessRights
  public countryCurrency = environment.countryCurrency
    ? environment.countryCurrency
    : '€'
  public BulkAction = BulkAction
  public showIsUsedColumn: boolean = false
  public isSalePaid$: Observable<boolean>
  public multidayEnabled$: Observable<boolean>

  constructor(
    public router: Router,
    private store: Store<AppState>,
    private translate: TranslateService,
    private modalService: BsModalService,
    private notificationService: NotificationService,
    private actions$: Actions,
    public saleItemService: SaleItemService,
    public saleService: SaleService
  ) {}

  ngOnInit() {
    this.saleId$ = this.saleId
      ? of(this.saleId)
      : this.store
          .select(saleSelectors.selectIdRouteParam)
          .pipe(filter((id) => Boolean(id)))

    this.sale$ = this.saleId$.pipe(
      switchMap((saleId) => this.store.select(saleSelectors.selectById, saleId))
    )
    this.multidayEnabled$ = this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType<boolean>(
          ConfigOptionsKeys.MULTIDAY_ENABLED
        )
      )
      .pipe(map(Boolean))

    this.saleItems$ = this.saleId$.pipe(
      switchMap((saleId) =>
        this.store.select(saleItemSelectors.selectByFilter, {
          field: 'saleId',
          value: saleId
        })
      ),
      map((saleItems) => {
        return saleItems.sort((a, b) => a.id - b.id)
      })
    )
    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    combineLatest([
      this.sale$,
      this.store.select(paymentSelectors.selectEntities)
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([sale, paymentEntities]) => {
        if (sale?.payments?.length) {
          let isUnpaidPayment = false
          let salePaymentsTotalAmount = 0
          sale?.payments?.forEach((paymentId) => {
            if (
              paymentEntities[paymentId]?.collectStatus ===
              CollectStatusType.COLLECT_STATUS_COLLECTED
            ) {
              salePaymentsTotalAmount += Number(
                paymentEntities[paymentId]?.amount
              )
            }

            if (paymentEntities[paymentId]?.collectStatus != 0) {
              isUnpaidPayment = true
            }
          })
        }
      })

    this.isSalePaid$ = combineLatest([
      this.sale$,
      this.saleId$.pipe(
        switchMap((saleId) =>
          this.store.select(paymentSelectors.selectByFilter, {
            field: 'saleId',
            value: saleId
          })
        )
      )
    ]).pipe(
      filter(([sale, _]) => Boolean(sale)),
      untilDestroyed(this),
      map(([sale, payments]) => {
        return this.saleService.isSalePaid(sale, payments)
      })
    )

    this.saleItems$
      .pipe(
        untilDestroyed(this),
        filter((saleItems) => Boolean(saleItems?.length)),
        take(1)
      )
      .subscribe((saleItems) => {
        this.store.dispatch(
          saleItemActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                saleId: saleItems[0].saleId
              },
              join: [
                { field: 'product' },
                { field: 'product.linkedSubscriptions' },
                { field: 'subscription' },
                { field: 'course' },
                { field: 'tags' },
                { field: 'rental' }
              ]
            })
          })
        )

        this.store
          .select(participationSelectors.selectAll)
          .pipe(untilDestroyed(this))
          .subscribe((participants) => {
            const saleItemWithParticipationsOrSubscription = saleItems.find(
              (saleItem) => {
                if (saleItem.subscriptionId) {
                  return true
                }

                const saleItemParticipations = participants.find(
                  (participant) => participant.saleItemId == saleItem.id
                )
                return Boolean(saleItemParticipations)
              }
            )

            this.showIsUsedColumn = !Boolean(
              saleItemWithParticipationsOrSubscription
            )
          })
      })
  }

  public onClickRow(saleItem: SaleItemExtended): void {
    combineLatest([this.productEntities$, this.sale$])
      .pipe(take(1))
      .subscribe(([productEntities, sale]) => {
        const product = productEntities[saleItem.productId]
        if (product?.spreadParticipantsOverResources) {
          this.router.navigate([`/sales/${this.saleId}/organize`])
          return
        }

        if (saleItem.bookingId) {
          this.router.navigate([`/bookings/${saleItem.bookingId}`], {
            state: { returnUrl: `/sales/${this.saleId}` }
          })
          return
        }

        if (saleItem.courseId) {
          this.router.navigate([`/courses/${saleItem.courseId}`])
          return
        }

        if (saleItem.subscriptionId && sale?.customerPersonId) {
          this.router.navigate([
            `/members/${sale.customerPersonId}/subscriptions`
          ])
          return
        }

        if (this.readonly) {
          return
        }

        if (saleItem['viewEvents']) {
          this.modalService.show(SaleItemEditsModalComponent, {
            initialState: {
              saleItem
            },
            class: 'modal-xl modal-dialog-scrollable sale-item-tab'
          })
        } else {
          this.modalService.show(SaleItemShowModalComponent, {
            initialState: {
              title: 'general.edit',
              saleItem
            },
            class: 'modal-xl modal-dialog-scrollable sale-item-tab'
          })
        }
      })
  }

  private getDescription(saleItem: Partial<SaleItem>): string {
    let description = ''

    if (saleItem.params) {
      if (saleItem.params.startDate) {
        description += `${moment(saleItem.params.startDate).format(
          'HH:mm'
        )} - ${moment(saleItem.params.endDate).format('HH:mm')} ${moment(
          saleItem.params.endDate
        ).format('DD-MM-YYYY')}`
      }

      if (saleItem.params.invitedMemberEmails) {
        description += ` invited members: ${saleItem.params.invitedMemberEmails.join(
          ', '
        )}`
      }
    }

    return description
  }
  public downloadCSV(type) {
    this.saleItems$
      .pipe(
        take(1),
        filter((saleItems) => Boolean(saleItems?.length))
      )
      .subscribe((saleItems) => {
        this.store.dispatch(
          saleItemActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                saleId: saleItems[0].saleId
              },
              join: [
                { field: 'product' },
                { field: 'sale' },
                { field: 'subscription' },
                { field: 'course' },
                { field: 'tags' },
                { field: 'bookingItems' }
              ]
            })
          })
        )
      })
    this.actions$
      .pipe(
        ofType(saleItemActions.upsertMany),
        take(1),
        timeout(1000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        let memberEntities
        this.store
          .select(memberSelectors.selectEntities)
          .pipe(take(1))
          .subscribe((m) => (memberEntities = m))

        let sale: Sale
        this.sale$.pipe(take(1)).subscribe((s) => (sale = s))

        let organization
        this.store
          .select(organizationSelectors.selectByFilter, {
            field: 'id',
            value: sale?.organizationId
          })
          .pipe(take(1))
          .subscribe((or) => (organization = or[0]))

        let sale_extract,
          sale_id,
          sale_date,
          sale_modification,
          sale_customer,
          sale_total,
          sale_vat,
          sale_header_activity,
          sale_header_resource,
          sale_header_date,
          sale_header_participants,
          sale_header_quantity,
          sale_header_total,
          sale_organization
        combineLatest([
          this.translate.get('general.pdf_sale_extract'),
          this.translate.get('general.pdf_sale_id'),
          this.translate.get('general.pdf_sale_date'),
          this.translate.get('general.pdf_sale_modification'),
          this.translate.get('general.pdf_sale_customer'),
          this.translate.get('general.pdf_sale_total'),
          this.translate.get('general.pdf_sale_vat'),
          this.translate.get('general.pdf_sale_header_activity'),
          this.translate.get('general.pdf_sale_header_resource'),
          this.translate.get('general.pdf_sale_header_date'),
          this.translate.get('general.pdf_sale_header_participants'),
          this.translate.get('general.pdf_sale_header_quantity'),
          this.translate.get('general.pdf_sale_header_total'),
          this.translate.get('general.pdf_sale_organization')
        ])
          .pipe(take(1))
          .subscribe(
            ([
              t_sale_extract,
              t_sale_id,
              t_sale_date,
              t_sale_modification,
              t_sale_customer,
              t_sale_total,
              t_sale_vat,
              t_sale_header_activity,
              t_sale_header_resource,
              t_sale_header_date,
              t_sale_header_participants,
              t_sale_header_quantity,
              t_sale_header_total,
              t_sale_organization
            ]) => {
              sale_extract = t_sale_extract
              sale_id = t_sale_id
              sale_date = t_sale_date
              sale_modification = t_sale_modification
              sale_customer = t_sale_customer
              sale_total = t_sale_total
              sale_vat = t_sale_vat
              sale_header_activity = t_sale_header_activity
              sale_header_resource = t_sale_header_resource
              sale_header_date = t_sale_header_date
              sale_header_participants = t_sale_header_participants
              sale_header_quantity = t_sale_header_quantity
              sale_header_total = t_sale_header_total
              sale_organization = t_sale_organization
            }
          )
        this.store
          .select(saleItemSelectors.selectAll)
          .pipe(
            take(1),
            map((saleitems) => {
              return saleitems.filter(
                (item) => item.saleId === Number(this.saleId)
              )
            }),
            distinctUntilChanged(
              (a, b) => JSON.stringify(a) == JSON.stringify(b)
            )
          )
          .subscribe((saleitems) => {
            let saleItemsGroupByProductPipe = new SaleItemsGroupByProductPipe(
              this.store
            ).transform(saleitems)

            saleItemsGroupByProductPipe
              .pipe(
                withLatestFrom(
                  this.store.select(bookingSelectors.selectEntities),
                  this.store.select(participationSelectors.selectAll),
                  this.store.select(bookingItemSelectors.selectEntities),
                  this.store.select(settingSelectors.selectById, 'language')
                ),
                take(1),
                distinctUntilChanged(
                  (a, b) => JSON.stringify(a) == JSON.stringify(b)
                )
              )
              .subscribe(
                ([
                  saleItemsByProductIdSets,
                  bookingEntities,
                  participations,
                  bookingItemEntities,
                  language
                ]) => {
                  // write headers
                  const docPdf: jsPDF = new jsPDF('l', 'mm', 'a4').setLanguage(
                    language
                  )
                  docPdf.text(sale_extract, 7, 15)
                  docPdf.setFontSize(8)
                  docPdf.text(`${sale_id} ${sale.id}`, 7, 20)
                  docPdf.text(
                    `${sale_date} :${
                      moment(sale.date).format('DD-MM-YYYY HH:mm') || ''
                    }`,
                    7,
                    24
                  )
                  docPdf.text(
                    `${sale_modification} :${
                      moment(sale.modificationDate).format(
                        'DD-MM-YYYY HH:mm'
                      ) || ''
                    }`,
                    7,
                    28
                  )
                  docPdf.text(
                    `${sale_customer} : ${
                      memberEntities[sale.customerPersonId]?.firstName || ''
                    } ${
                      memberEntities[sale.customerPersonId]?.middleName || ''
                    } ${memberEntities[sale.customerPersonId]?.lastName || ''}`,
                    7,
                    32
                  )
                  docPdf.text(
                    `${sale_organization} :${organization?.description || ''}`,
                    7,
                    36
                  )

                  docPdf.text(
                    `${sale_total} : ${this.countryCurrency}${sale.total} `,
                    7,
                    40
                  )
                  docPdf.text(
                    `${sale_vat} : ${this.countryCurrency}${sale.vat} `,
                    45,
                    40
                  )
                  let bodyTable: any = []

                  // prep table rows
                  let bookingRelatedRows = []
                  let nonBookingRows = []

                  for (const saleItemsByProductIdSet of saleItemsByProductIdSets) {
                    const saleItems = saleItemsByProductIdSet.saleItems
                    const saleItemParticipants = participations.filter((p) =>
                      saleItems.find((saleItem) =>
                        saleItem.bookingId
                          ? p.saleItemId == saleItem.id &&
                            p.bookingId == saleItem.bookingId
                          : p.saleItemId == saleItem.id
                      )
                    )

                    const saleItemBookings = saleItemParticipants
                      .reduce((result, participant) => {
                        const booking = bookingEntities[participant.bookingId]

                        if (!result.find((b) => b.id == booking.id)) {
                          result.push(booking)
                        }
                        return result
                      }, [])
                      .sort((a, b) =>
                        a.startDate.valueOf() < b.startDate.valueOf() ? -1 : 1
                      )

                    if (
                      !saleItemParticipants?.length ||
                      !saleItemBookings?.length
                    ) {
                      saleItems.map((saleItem) => {
                        let totalParticipants = 0
                        if (
                          saleItem.hasOwnProperty('params') &&
                          !!saleItem.params &&
                          saleItem.params.invitedGuests
                        ) {
                          totalParticipants =
                            saleItem.params.invitedGuests.length +
                            totalParticipants
                        }
                        if (
                          saleItem.hasOwnProperty('params') &&
                          !!saleItem.params &&
                          saleItem.params.invitedOthers
                        ) {
                          totalParticipants =
                            saleItem.params.invitedOthers.length +
                            totalParticipants
                        }
                        if (
                          saleItem.hasOwnProperty('params') &&
                          !!saleItem.params &&
                          saleItem.params.invitedMemberEmails
                        ) {
                          totalParticipants =
                            saleItem.params.invitedMemberEmails.length +
                            totalParticipants
                        }
                        if (saleItem.bookingItems?.length) {
                          saleItem.bookingItems.map((bookingItemId) => {
                            const bookingItem =
                              bookingItemEntities[bookingItemId]

                            nonBookingRows.push({
                              sortByDate: bookingItem.startDate,
                              activity: saleItemsByProductIdSet.description,
                              resource:
                                saleItemsByProductIdSet.resources.split('[')[0],
                              date: `${moment(bookingItem.startDate).format(
                                'HH:mm DD-MM-YYYY'
                              )} - ${moment(bookingItem.endDate).format(
                                'DD-MM-YYYY'
                              )} ${moment(bookingItem.endDate).format(
                                'HH:mm'
                              )}`,
                              quantity: saleItem.quantity,
                              total: `${this.countryCurrency} ${
                                Math.round(
                                  (Number(saleItem.totalPrice) +
                                    Number.EPSILON) *
                                    100
                                ) / 100
                              }`,
                              participants: saleItemParticipants.length
                                ? saleItemParticipants.length
                                : totalParticipants
                            })
                          })
                        } else {
                          nonBookingRows.push({
                            sortByDate: saleItem.params?.startDate ?? null,
                            activity: saleItemsByProductIdSet.description,
                            resource:
                              saleItemsByProductIdSet.resources.split('[')[0],
                            date: !saleItem?.description?.includes('Invalid')
                              ? saleItem.description
                              : this.getDescription(saleItem),
                            quantity: saleItem.quantity,
                            total: `${this.countryCurrency} ${
                              Math.round(
                                (Number(saleItem.totalPrice) + Number.EPSILON) *
                                  100
                              ) / 100
                            }`,
                            participants: saleItemParticipants.length
                              ? saleItemParticipants.length
                              : totalParticipants
                          })
                        }
                      })
                    } else {
                      let bookingsAreInSequence = true
                      if (saleItemBookings.length > 1) {
                        for (let i = 1; i < saleItemBookings.length; i++) {
                          const diffInHours = moment(
                            saleItemBookings[i].startDate
                          ).diff(saleItemBookings[i - 1].startDate, 'hours')

                          if (diffInHours > 25) {
                            bookingsAreInSequence = false
                            break
                          }
                        }
                      }

                      if (!bookingsAreInSequence) {
                        saleItems.map((saleItem) => {
                          //   saleItemBookings.map((booking) => {
                          bookingRelatedRows.push({
                            sortByDate:
                              bookingEntities[saleItem.bookingId].startDate,
                            activity: saleItemsByProductIdSet.description,
                            resource:
                              saleItemsByProductIdSet.resources.split('[')[0],
                            date: `${moment(
                              bookingEntities[saleItem.bookingId].startDate
                            ).format('HH:mm DD-MM-YYYY')} - ${moment(
                              bookingEntities[saleItem.bookingId].endDate
                            ).format('DD-MM-YYYY')} ${moment(
                              bookingEntities[saleItem.bookingId].endDate
                            ).format('HH:mm')}`,
                            quantity: saleItem.quantity,
                            total: `${this.countryCurrency} ${
                              Math.round(
                                (Number(saleItem.totalPrice) + Number.EPSILON) *
                                  100
                              ) / 100
                            }`,
                            participants:
                              bookingEntities[saleItem.bookingId].participations
                                .length
                          })
                          // })
                        })
                      } else {
                        let totalParticipants = 0
                        saleItems.map((saleItem) => {
                          if (
                            saleItem.hasOwnProperty('params') &&
                            !!saleItem.params &&
                            saleItem.params.invitedGuests
                          ) {
                            totalParticipants =
                              saleItem.params.invitedGuests.length +
                              totalParticipants
                          }
                          if (
                            saleItem.hasOwnProperty('params') &&
                            !!saleItem.params &&
                            saleItem.params.invitedOthers
                          ) {
                            totalParticipants =
                              saleItem.params.invitedOthers.length +
                              totalParticipants
                          }
                          if (
                            saleItem.hasOwnProperty('params') &&
                            !!saleItem.params &&
                            saleItem.params.invitedMemberEmails
                          ) {
                            totalParticipants =
                              saleItem.params.invitedMemberEmails.length +
                              totalParticipants
                          }
                        })
                        const firstBooking = saleItemBookings[0]
                        const lastBooking =
                          saleItemBookings[saleItemBookings.length - 1]

                        const saleItem = saleItems[0]
                        bookingRelatedRows.push({
                          sortByDate: firstBooking.startDate,
                          activity: saleItemsByProductIdSet.description,
                          resource:
                            saleItemsByProductIdSet.resources.split('[')[0],
                          date: `${moment(firstBooking.startDate).format(
                            'HH:mm DD-MM-YYYY'
                          )} - ${moment(lastBooking.endDate).format(
                            'DD-MM-YYYY'
                          )} ${moment(lastBooking.endDate).format('HH:mm')}`,
                          quantity: saleItem.quantity,
                          total: `${this.countryCurrency} ${
                            Math.round(
                              (Number(
                                saleItems.reduce(
                                  (total, saleItem) =>
                                    (total += <any>saleItem.totalPrice),
                                  0
                                )
                              ) +
                                Number.EPSILON) *
                                100
                            ) / 100
                          }`,

                          participants: saleItemParticipants.length
                            ? saleItemParticipants.length
                            : totalParticipants
                        })
                      }
                    }
                  }

                  bookingRelatedRows
                    .sort((a, b) => (a.sortByDate < b.sortByDate ? -1 : 1))
                    .map((row) => bodyTable.push(row))

                  bodyTable.push(['', '', '', '', '', ''])

                  nonBookingRows
                    .sort((a, b) =>
                      moment(a.sortByDate).isAfter(b.sortByDate) ? -1 : 1
                    )
                    .map((row) => bodyTable.push(row))

                  const options: any = {
                    body: bodyTable,
                    columns: [
                      { header: sale_header_activity, dataKey: 'activity' },
                      { header: sale_header_resource, dataKey: 'resource' },
                      { header: sale_header_date, dataKey: 'date' },
                      {
                        header: sale_header_participants,
                        dataKey: 'participants'
                      },
                      { header: sale_header_quantity, dataKey: 'quantity' },
                      { header: sale_header_total, dataKey: 'total' }
                    ],
                    columnStyles: [
                      { cellWidth: 'wrap' },
                      { cellWidth: 68 },
                      { cellWidth: 'wrap' },
                      { cellWidth: 'wrap' },
                      { cellWidth: 'wrap' },
                      { cellWidth: 'wrap' }
                    ],
                    startY: 45,
                    margin: { horizontal: 7 },

                    styles: { fontSize: 7, maxCellHeight: 8 }
                  }
                  autoTable(docPdf, options)

                  docPdf.save(`extract_sale_#${this.saleId}`)
                }
              )
          })
      })
  }
  public onClickDelete(saleItem: SaleItem, showSuccessMsg: boolean = true) {
    if (!saleItem) {
      this.notificationService.emit('alerts.error', 'error')
      return
    }

    if (this.readonly) {
      return
    }
    if (showSuccessMsg) {
      const message = this.translate.instant('saleModule.deleteSaleProduct')
      if (!confirm(message)) {
        return
      }
    }

    this.store.dispatch(
      saleItemActions.deleteOne({
        id: saleItem.id
      })
    )

    this.actions$
      .pipe(
        ofType(saleItemActions.removeOne),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (!action) {
          return null
        }

        this.store.dispatch(
          saleActions.fetchOneById({
            id: saleItem.saleId,
            params: RequestQueryBuilder.create({
              join: [{ field: 'creditSales' }, { field: 'payments' }]
            })
          })
        )
        if (showSuccessMsg)
          this.notificationService.emit('alerts.success', 'success')
      })
  }

  public onClickAddSaleItem() {
    this.saleId$
      .pipe(
        take(1),
        withLatestFrom(this.store.select(saleSelectors.selectEntities))
      )
      .subscribe(([saleId, saleEntities]) => {
        const sale = saleEntities?.[saleId]

        this.modalService.show(SaleItemCreateModalComponent, {
          initialState: {
            title: 'general.item',
            saleItem: new SaleItem(),
            saleId,
            selectedCustomerPersonId: sale?.customerPersonId
          },
          class: 'modal-lg modal-dialog-scrollable'
        })
      })
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    if (bulkActionEvent.items.length == 0) {
      return
    }
    if (
      bulkActionEvent.action == BulkAction.SET_IS_USED ||
      bulkActionEvent.action == BulkAction.SET_IS_NOT_USED
    ) {
      this.actions$
        .pipe(
          ofType(saleItemActions.upsertOne, saleItemActions.upsertMany),
          take(1),
          timeout(1000),
          catchError((error) => of(null))
        )
        .subscribe((action) => {
          if (action) {
            this.notificationService.emit('alerts.success', 'success')
          }
        })

      bulkActionEvent.items.forEach((item) => {
        this.store.dispatch(
          saleItemActions.updateOneById({
            id: item.id,
            params: {
              claimed:
                bulkActionEvent.action == BulkAction.SET_IS_USED ? true : false
            }
          })
        )
      })
    }
    if (bulkActionEvent.action == BulkAction.CREATE_SEPARATE_SALE) {
      let saleIds = [
        ...new Set(bulkActionEvent.items.map((item) => item.saleId))
      ]
      if (saleIds.length > 0) {
        this.actions$
          .pipe(
            ofType(paymentActions.upsertMany),
            take(1),
            withLatestFrom(this.store.select(authSelectors.selectMember)),
            catchError(() => of(null))
          )
          .subscribe(([action, authMember]) => {
            if (!action) {
              this.notificationService.emit(
                'alerts.operationNotMade',
                'warning'
              )
              return
            }
            if (action.entities && action.entities.length == 0) {
              this.notificationService.emit(
                'alerts.operationSaleUnpaidError',
                'warning'
              )
              return
            }
            let confirmMsg = ''
            this.translate
              .get('saleModule.confirmCreateSeparateSale')
              .pipe(take(1))
              .subscribe((msg) => (confirmMsg = msg))
            if (!confirm(confirmMsg)) {
              return
            }
            let saleItems = []
            bulkActionEvent.items.forEach((element) => {
              saleItems.push(element.id)
            })
            this.actions$
              .pipe(
                ofType(saleActions.upsertMany),
                take(1),
                timeout(2000),

                catchError(() => of(null)),
                withLatestFrom(this.sale$)
              )
              .subscribe(([action, sale]) => {
                if (!action) {
                  if (sale)
                    this.store.dispatch(
                      saleActions.fetchOneById({
                        id: sale.id,
                        params: RequestQueryBuilder.create({
                          join: [
                            { field: 'creditSales' },
                            { field: 'payments' }
                          ]
                        })
                      })
                    )
                  else return
                }
                if (saleItems.length > 0)
                  this.store.dispatch(
                    saleItemActions.removeMany({ ids: saleItems })
                  )
                this.notificationService.emit('alerts.success', 'success')
                const url = this.router.serializeUrl(
                  this.router.createUrlTree([
                    `/sales/${action.entities[1]?.id}`
                  ])
                )
                if (action.entities[1]?.id)
                  this.store.dispatch(
                    saleActions.fetchOneById({
                      id: action.entities[1]?.id,
                      params: RequestQueryBuilder.create({
                        join: [
                          { field: 'creditSales' },
                          { field: 'saleItems' },
                          { field: 'saleItems.booking' },
                          { field: 'saleItems.tags' },
                          { field: 'saleItems.booking.product' },
                          { field: 'saleItems.booking.linkedProduct' },
                          { field: 'saleItems.product' },
                          { field: 'saleItems.product.translations' },
                          { field: 'saleItems.product.parentBookableProducts' },
                          { field: 'saleItems.participations' },
                          { field: 'payments' }
                        ]
                      })
                    })
                  )
                setTimeout(() => {
                  this.store.dispatch(
                    saleActions.fetchOneById({
                      id: sale.id,
                      params: RequestQueryBuilder.create({
                        join: [
                          { field: 'creditSales' },
                          { field: 'saleItems' },
                          { field: 'saleItems.booking' },
                          { field: 'saleItems.tags' },
                          { field: 'saleItems.booking.product' },
                          { field: 'saleItems.booking.linkedProduct' },
                          { field: 'saleItems.product' },
                          { field: 'saleItems.product.translations' },
                          { field: 'saleItems.product.parentBookableProducts' },
                          { field: 'saleItems.participations' },
                          { field: 'payments' }
                        ]
                      })
                    })
                  )
                }, 500)

                window.open(url, '_blank')
              })
            this.sale$.pipe(take(1)).subscribe((originalSale) => {
              this.store.dispatch(
                saleActions.createSeparateSale({
                  saleId: originalSale.id,
                  saleItems: saleItems
                })
              )
            })
          })
        this.store.dispatch(
          paymentActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                saleId: { [CondOperator.IN]: saleIds },
                $and: [
                  { payMethod: { [CondOperator.IN]: [PayMethod.UNDECIDED] } }
                ]
              }
            })
          })
        )
      }
    }
  }

  public onEditBulkAction(event) {
    if (event?.length > 0) {
      let saleItems = []
      this.saleItems$.pipe(take(1)).subscribe((saleIt) => (saleItems = saleIt))
      const groupById = event[0].productId
      const updateSaleItems = event.map((it) =>
        saleItems.find((ite) => ite.id == it.id)
      )

      const modalRef = this.modalService.show(SaleItemShowModalComponent, {
        initialState: {
          title: 'saleModule.editBulkActionTitle',
          saleItemsOnBulkActions: updateSaleItems.sort((a, b) =>
            a.quantity > b.quantity ? -1 : 1
          )
        },
        class: 'modal-xl modal-dialog-scrollable sale-item-tab',
        backdrop: 'static'
      })
      modalRef.onHide.pipe(take(1)).subscribe((modal) => {
        this.resetAllChecked$.next([groupById, false])
        setTimeout(() => {
          this.store.dispatch(
            saleActions.fetchOneById({
              id: this.saleId,
              params: RequestQueryBuilder.create({
                join: [
                  { field: 'creditSales' },
                  { field: 'saleItems' },
                  { field: 'saleItems.booking' },
                  { field: 'saleItems.tags' },
                  { field: 'saleItems.booking.product' },
                  { field: 'saleItems.booking.linkedProduct' },
                  { field: 'saleItems.product' },
                  { field: 'saleItems.product.translations' },
                  { field: 'saleItems.product.parentBookableProducts' },
                  { field: 'saleItems.participations' },
                  { field: 'payments' }
                ]
              })
            })
          )
        }, 500)
      })
    }
  }
}
